/**
 * A closure: an operational surface taken out of use, planned or not.
 *
 * The platform records them with the zone, the reason, the extent, when they
 * started and ended, and the work order they were declared for where there
 * was one. Minutes are the platform's own arithmetic. See `integrate.py` and
 * `compliance/availability.py` on the backend.
 */
export interface Closure {
  id: string;
  reference?: string;
  kind: "planned" | "unplanned";
  extent: "full" | "partial";
  reason: string;
  reasonCode?: string;
  status: string;
  /**
   * When it took effect. A closure waiting to start (status planned) has not;
   * it is listed only when asked for (`includePlanned`), and then this is its
   * planned start.
   */
  startedAt: string;
  endedAt?: string;
  plannedStart?: string;
  plannedEnd?: string;
  fromChainageM?: number;
  toChainageM?: number;
  zoneCode?: string;
  zoneId?: string;
  workOrder?: { id: string; number?: string; title?: string };
  /** how long it was closed, in minutes; the platform's figure */
  minutes?: number;
  /** what the recorder attached — on a map closure: the NOTAM, the surface, fixed times */
  attrs?: Record<string, unknown>;
  /** when the platform took the record, and who recorded and who lifted it, as the platform stamped them */
  declaredAt?: string;
  declaredBy?: string;
  endedBy?: string;
}

/** How a closure list is asked for. */
export interface ListClosuresOptions {
  /**
   * Also list closures that have not started — scheduled ones, and ones lifted
   * before they began. Off by default: the insight screens count what
   * happened, and a closure that has not happened has no day to count on.
   */
  includePlanned?: boolean;
}

/** Closing a surface (POST /v1/closures). The platform stamps who and when. */
export interface DeclareClosureInput {
  siteId: string;
  /** the surface, e.g. RWY-18-36 or TWY-J1; one the site does not have is refused */
  zoneCode: string;
  reason: string;
  kind: "planned" | "unplanned";
  /** the NOTAM number */
  reference: string;
  /** set when it is already in force */
  startedAt?: string;
  /** set when it starts later */
  plannedStart?: string;
  /** null: until further notice */
  plannedEnd?: string | null;
  attrs?: Record<string, unknown>;
}

export interface DeclaredClosure {
  id: string;
  /** "planned" when it starts later, otherwise "active" */
  status: string;
  startedAt: string | null;
  reference?: string;
}

export interface EndedClosure {
  endedAt: string;
  minutes: number;
}

/**
 * The surface code the platform knows a map surface by: runway 18/36 is
 * RWY-18-36, taxiway J1 is TWY-J1.
 */
export function surfaceCode(kind: "runway" | "taxiway", name: string): string {
  const n = name.trim().toUpperCase();
  return kind === "runway" ? `RWY-${n.replace(/\s*\/\s*/g, "-")}` : `TWY-${n}`;
}

/** The other way: a zone code as a map surface, or null for a zone that is neither a runway nor a taxiway. */
export function surfaceOf(code: string | undefined): { kind: "runway" | "taxiway"; name: string } | null {
  if (!code) return null;
  const rwy = /^RWY-(\d{2}[LRC]?)-(\d{2}[LRC]?)$/i.exec(code.trim());
  if (rwy) return { kind: "runway", name: `${rwy[1]}/${rwy[2]}`.toUpperCase() };
  const twy = /^TWY-([A-Z]{1,2}\d{0,2})$/i.exec(code.trim());
  if (twy) return { kind: "taxiway", name: twy[1].toUpperCase() };
  return null;
}
